/**
 * 관리자 세션 보관 + 구독.
 *
 * 서버 토큰은 서명된 문자열이라 서버에 세션 상태가 없다 → 로그아웃은 저장한 토큰을
 * 지우는 것으로 끝난다. 만료 시각을 함께 저장해 두고, 만료된 세션은 읽는 순간 버린다
 * (만료 토큰으로 요청을 보내 401 을 받고 나서야 알게 되는 상황을 줄인다).
 *
 * localStorage 는 브라우저 설정에 따라 접근 자체가 예외를 던질 수 있어(시크릿 모드 등)
 * 모든 접근을 try/catch 로 감싼다. 실패하면 이 탭 안에서만 유지되는 세션이 된다.
 */
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "unearth.admin.session";

export interface AdminSession {
  token: string;
  /** 만료 시각(epoch 초) — 서버 응답의 expires_at 을 그대로 보관한다. */
  expiresAt: number;
  username: string;
}

let cached: AdminSession | null = null;
let loaded = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function isExpired(session: AdminSession, now = Date.now()): boolean {
  return session.expiresAt * 1000 <= now;
}

function readStorage(): AdminSession | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // 저장소 접근 차단 — 이 탭 메모리에만 세션이 남는다.
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AdminSession>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.expiresAt !== "number" ||
      typeof parsed.username !== "string"
    ) {
      return null;
    }
    return {
      token: parsed.token,
      expiresAt: parsed.expiresAt,
      username: parsed.username,
    };
  } catch {
    return null; // 손상된 값 — 없는 것으로 본다.
  }
}

function writeStorage(session: AdminSession | null): void {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 저장 실패는 치명적이지 않다 — 메모리 캐시로 이 탭에서는 계속 동작한다.
  }
}

/** 현재 세션. 만료됐으면 지우고 null 을 준다. */
export function getSession(): AdminSession | null {
  if (!loaded) {
    cached = readStorage();
    loaded = true;
  }
  if (cached && isExpired(cached)) {
    cached = null;
    writeStorage(null);
  }
  return cached;
}

export function setSession(session: AdminSession): void {
  cached = session;
  loaded = true;
  writeStorage(session);
  notify();
}

export function clearSession(): void {
  const had = getSession() !== null;
  cached = null;
  loaded = true;
  writeStorage(null);
  if (had) notify();
}

export function isLoggedIn(): boolean {
  return getSession() !== null;
}

/** 관리자 전용 요청에 실을 헤더. 세션이 없으면 빈 객체. */
export function authHeaders(): Record<string, string> {
  const session = getSession();
  return session ? { Authorization: `Bearer ${session.token}` } : {};
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 로그인 상태를 구독하는 훅. 로그인/로그아웃/만료 시 리렌더된다. */
export function useSession(): AdminSession | null {
  return useSyncExternalStore(subscribe, getSession, () => null);
}
